// Shared types
import {
    Map,
    Floor,
    RequestSpace,
    InsulationLevel,
    OutdoorUnitLocation,
} from '@coldtec/cos-types';

// Tool types
import { Errors, Nullable } from '@/types';

/**
 *  Available store action for the spaces.
 */
export enum SpacesAction {
    /** Used to fetch and store all the data that is used to configure a space. */
    FETCH_CONFIG_DATA = 'spaces:fetchConfigData',

    /** Used to sync the spaces of the request with the tool spaces. */
    SYNC_FROM_REQUEST = 'spaces:sync:fromRequest',

    /** Used to add a new space. */
    ADD = 'spaces:add',

    /** Used to reset the system selection of a space. */
    RESET_SYSTEM_SELECTION = 'spaces:reset:systemSelection',

    /** Used to remove a space (based on local id). */
    REMOVE = 'spaces:remove',

    /**
     *
     *  Used to check if all spaces are valid.
     *
     *  (i) Validation of a single space happens in the `Spaces.vue` component.
     *
     */
    CHECK_VALIDITY = 'spaces:checkValidity',

    /**
     *
     *  Used to select an indoor unit for a space.
     *
     *  (i) Also enables the invoker to reset a space's indoor unit by providing
     *  his local space id in the `resetLocalSpaceId`. This is needed because
     *  the `localSpaceId` will be `null` if it got reset, making it
     *  impossible to know which space should be resetted.
     *
     */
    SELECT_INDOOR_UNIT = 'spaces:select:indoorUnit',

    /** Used to select a color for the space's indoor unit. */
    SELECT_INDOOR_UNIT_COLOR = 'spaces:select:indoorUnit:color',

    /** Used to toggle a system accessory for the spaces for which the systems are currently shown. */
    TOGGLE_ACCESSORY = 'spaces:select:accessory',
}

/**
 *  Available store getter for the spaces.
 */
export enum SpacesGetter {
    /** Used to get the added spaces. */
    ADDED = 'spaces:added',

    /** Used to get the amount of added spaces. */
    COUNT_ADDED = 'spaces:count:added',

    /**
     *
     *  Used to get the amount of valid spaces.
     *
     *  (i) Ensuring a system, indoor unit and color has been set
     *
     */
    COUNT_VALID = 'spaces:count:valid',
}

/**
 *  Available store mutation for the spaces.
 */
export enum SpacesMutation {
    /** Used to store the config data of the spaces. */
    CONFIG_DATA = 'spaces:configData',

    /** Used to add a new space. */
    ADD = 'spaces:add',

    /** Used to reset the system selection of a space. */
    RESET_SYSTEM_SELECTION = 'spaces:reset:systemSelection',

    /** Used to remove a space (based on local id). */
    REMOVE = 'spaces:remove',

    /** Used to toggle the open state of a space. */
    TOGGLE = 'spaces:toggle',

    /** Used to update the overall validity of the spaces. */
    VALIDITY = 'spaces:validity',
}

/**
 *  Holding the wrapped space object, including additional tool state.
 */
export interface ToolSpace {
    /** Holding all the data of the space to compute a system suggestion for. */
    data: Nullable<RequestSpace>;

    /** Holding all the errors of the data. */
    errors: Errors<RequestSpace>;

    /** Holding the open state of the space. */
    open: boolean;

    /** Holding the valid state of the space. */
    valid: boolean;
}

/**
 *  Holding the store's spaces state.
 */
export interface SpacesState {
    /** Holding a map of all the added tool spaces, keyed on local id. */
    added: Map<ToolSpace>;

    /** Holding all the data that is needed to configure a space. */
    configData: SpacesConfigData;

    /** Holding the overall valid state of the step / all spaces. */
    valid: boolean;
}

/**
 *  Holding all the data that is needed to configure a space.
 */
export interface SpacesConfigData {
    /** Holding a map of all the available floors. */
    floors: Map<Floor>;

    /** Holding a map of all the available insulation levels. */
    insulation_levels: Map<InsulationLevel>;

    /** Holding a map of all the installation locations. */
    outdoor_unit_locations: Map<OutdoorUnitLocation>;
}
